/**
 * Installed-app and push readiness for the member portal (doorbell live calls
 * spec, "Installable portal"). On iPhone and iPad, Safari delivers web push
 * only to a web app added to the Home Screen (iOS 16.4+), and the subscription
 * must be requested from inside that app — so outside it the portal says
 * "Add to Home Screen first" instead of asking for a permission it can't get.
 * Pure functions over a `PwaEnv` snapshot, so they test in node.
 */

export interface PwaEnv {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  /** iOS Safari's own flag for a Home Screen web app. */
  navigatorStandalone: boolean;
  /** `(display-mode: standalone)`: the installed app on every platform. */
  displayModeStandalone: boolean;
}

export function readPwaEnv(): PwaEnv {
  if (typeof navigator === "undefined" || typeof window === "undefined") {
    return { userAgent: "", platform: "", maxTouchPoints: 0, navigatorStandalone: false, displayModeStandalone: false };
  }
  const nav = navigator as Navigator & { standalone?: boolean };
  return {
    userAgent: nav.userAgent,
    platform: nav.platform,
    maxTouchPoints: nav.maxTouchPoints ?? 0,
    navigatorStandalone: nav.standalone === true,
    displayModeStandalone: typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches,
  };
}

/** iPhone, iPod or iPad — iPadOS Safari reports a Mac (`MacIntel`) but has touch. */
export function isIos(env: PwaEnv): boolean {
  return /iPad|iPhone|iPod/.test(env.userAgent) || (env.platform === "MacIntel" && env.maxTouchPoints > 1);
}

export function isInstalled(env: PwaEnv): boolean {
  return env.displayModeStandalone || env.navigatorStandalone;
}

/** iOS Safari outside the installed app: web push can't work here. */
export function needsHomeScreen(env: PwaEnv): boolean {
  return isIos(env) && !isInstalled(env);
}

export type PushStatus = "needs-home-screen" | "unsupported" | "blocked" | "on" | "off";

export function browserPushStatus(input: {
  needsHomeScreen: boolean;
  supported: boolean;
  permission: NotificationPermission | null;
  subscribed: boolean;
}): PushStatus {
  if (input.needsHomeScreen) return "needs-home-screen";
  if (!input.supported) return "unsupported";
  if (input.permission === "denied") return "blocked";
  return input.subscribed ? "on" : "off";
}
